import type { QueryClient } from "@tanstack/react-query";
import { serviceDeskFormilyTicketGridQueryKey } from "@sd/services/APIs";

export function extractListArray(payload: unknown): unknown[] {
  if (!payload) {
    return [];
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  if (typeof payload !== "object") {
    return [];
  }
  const keys = [
    "grid_data",
    "results",
    "data",
    "tickets",
    "items",
    "records",
    "rows",
    "list",
    "content",
    "employees",
    "employee_list",
    "users",
  ];
  const fromRecord = (o: Record<string, unknown>): unknown[] | null => {
    for (const k of keys) {
      const v = o[k];
      if (Array.isArray(v)) {
        return v;
      }
    }
    return null;
  };
  const o = payload as Record<string, unknown>;
  const flat = fromRecord(o);
  if (flat) {
    return flat;
  }
  for (const nest of ["data", "payload", "response", "result"]) {
    const inner = o[nest];
    if (Array.isArray(inner)) {
      return inner;
    }
    if (inner && typeof inner === "object") {
      const nested = fromRecord(inner as Record<string, unknown>);
      if (nested) {
        return nested;
      }
    }
  }
  return [];
}

export function findGridRowRecord(
  queryClient: QueryClient,
  ticketId: string
): Record<string, unknown> | undefined {
  const matches = queryClient.getQueriesData<unknown>({
    queryKey: serviceDeskFormilyTicketGridQueryKey,
  });
  for (const [, data] of matches) {
    for (const row of extractListArray(data)) {
      if (!row || typeof row !== "object") {
        continue;
      }
      const r = row as Record<string, unknown>;
      const rid = String(
        r.id ?? r.ticket_id ?? r.reference ?? r.pk ?? ""
      ).trim();
      if (rid === ticketId) {
        return r;
      }
    }
  }
  return undefined;
}

/** Merge PATCH fields into cached `grid_data` (or other list arrays on the payload) — avoids stale list overwriting `assigned_to` after PUT. */
function patchGridPayloadRowsForTicket(
  oldData: unknown,
  ticketId: string,
  patch: Record<string, unknown>
): unknown {
  if (!oldData || typeof oldData !== "object") {
    return oldData;
  }
  let anyMatched = false;
  const o = { ...(oldData as Record<string, unknown>) };
  for (const [key, val] of Object.entries(o)) {
    if (!Array.isArray(val)) {
      continue;
    }
    const mapped = val.map((row: unknown) => {
      if (!row || typeof row !== "object") {
        return row;
      }
      const r = row as Record<string, unknown>;
      const rid = String(
        r.id ?? r.ticket_id ?? r.reference ?? r.pk ?? ""
      ).trim();
      if (rid !== ticketId) {
        return row;
      }
      anyMatched = true;
      return { ...r, ...patch };
    });
    o[key] = mapped;
  }
  return anyMatched ? o : oldData;
}

export function patchTicketInFormilyGridCaches(
  queryClient: QueryClient,
  ticketId: string,
  patch: Record<string, unknown>
): void {
  queryClient.setQueriesData<unknown>(
    { queryKey: serviceDeskFormilyTicketGridQueryKey },
    (old: unknown) => patchGridPayloadRowsForTicket(old, ticketId, patch)
  );
}

/** Detail GET misses fields sometimes; hydrate from cached grid row so PUT does not wipe them. */
export function mergeStaleFieldsFromGridCache(
  queryClient: QueryClient,
  ticketId: string,
  base: Record<string, unknown>
): Record<string, unknown> {
  const cached = findGridRowRecord(queryClient, ticketId);
  if (!cached) {
    return base;
  }
  const out = { ...base };
  const req = cached.requester ?? cached.requester_id;
  if (
    req !== undefined &&
    req !== null &&
    String(req).trim() !== "" &&
    (out.requester === undefined ||
      out.requester === null ||
      out.requester === "")
  ) {
    out.requester = req;
  }
  const asgn = cached.assigned_to ?? cached.assignee;
  if (
    asgn !== undefined &&
    asgn !== null &&
    String(asgn).trim() !== "" &&
    (out.assigned_to === undefined ||
      out.assigned_to === null ||
      out.assigned_to === "")
  ) {
    out.assigned_to = asgn;
  }
  return out;
}

export function pickServerFieldsForGridPatch(
  updated: Record<string, unknown>
): Record<string, unknown> {
  const keys = [
    "assigned_to",
    "status",
    "updated_on",
    "requester",
    "sla",
    "priority",
    "ui_id",
  ] as const;
  const out: Record<string, unknown> = {};
  for (const k of keys) {
    if (Object.prototype.hasOwnProperty.call(updated, k)) {
      const v = updated[k];
      if (v !== undefined) {
        out[k] = v;
      }
    }
  }
  return out;
}
